import React, { useState, useEffect, useCallback } from "react";
import { NavLink, useLocation, Outlet, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { NAV_GROUPS } from "@/config/nav";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { useHelp } from "@/components/help/HelpProvider";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NotificationBell } from "@/components/NotificationBell";
import { AccessibilityToolbar } from "@/components/AccessibilityToolbar";
import { DocTriageMenu } from "@/components/DocTriageMenu";

const Icon = ({ name, ...props }) => {
  const C = Icons[name] || Icons.Circle;
  return <C {...props} />;
};

const useDocReviewCount = () => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try { const { data } = await api.get("/doc-center/sections"); if (alive) setCount(data.sections?.needs_review || 0); }
      catch { /* non-staff / error: no badge */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  return count;
};

const NavContent = ({ onNavigate, docReview = 0 }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = !!user?.is_admin;
  const isSuperAdmin = user?.role === "super_admin";
  return (
    <nav className="flex flex-col gap-5 px-3 py-4">
      {NAV_GROUPS.filter((g) => !g.adminOnly || isAdmin).map((group) => {
        const items = group.items.filter((i) => (!i.superAdminOnly || isSuperAdmin) && (!i.adminOnly || isAdmin));
        if (!items.length) return null;
        return (
        <div key={group.label}>
          <div className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-white/45">
            {group.label}
          </div>
          <div className="flex flex-col gap-0.5">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onNavigate}
                end={item.to === "/documents"}
                data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium min-h-[42px] transition-colors ${
                    isActive
                      ? "bg-white/[0.14] text-white ring-1 ring-white/15"
                      : "text-white/75 hover:bg-white/10 hover:text-white"
                  }`
                }
              >
                <Icon name={item.icon} size={18} className="shrink-0" />
                <span className="truncate flex-1">{item.label}</span>
                {item.badge === "doc_review" && docReview > 0 && (
                  <span
                    data-testid="nav-doc-review-badge"
                    title={`${docReview} document${docReview !== 1 ? "s" : ""} need review`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNavigate?.(); navigate("/documents/review"); }}
                    className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-[#9C1E22] text-white text-[11px] font-bold flex items-center justify-center hover:bg-[#7d1319]"
                  >
                    {docReview > 99 ? "99+" : docReview}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        </div>
        );
      })}
    </nav>
  );
};

const Sidebar = ({ docReview }) => (
  <aside
    className="hidden lg:flex lg:flex-col w-64 shrink-0 h-screen sticky top-0 mmm-scroll overflow-y-auto"
    style={{ backgroundColor: "var(--mmm-navy)" }}
  >
    <div className="px-4 py-4 border-b border-white/10 sticky top-0 z-10" style={{ backgroundColor: "var(--mmm-navy)" }}>
      <div className="text-white">
        <Logo size={38} withText textClass="text-white" />
      </div>
    </div>
    <NavContent docReview={docReview} />
    <div className="mt-auto px-4 py-3 text-[11px] text-white/40 border-t border-white/10">
      Senior Transitions CRM · v1
    </div>
  </aside>
);

const titleFromPath = (pathname) => {
  for (const g of NAV_GROUPS) {
    const item = g.items.find((i) => i.to === pathname);
    if (item) return item.label;
  }
  return "Command Center";
};

const BranchSelector = () => {
  const { branches, activeBranch, chooseBranch, user } = useAuth();
  if (!branches?.length) return null;
  if (branches.length === 1) {
    return (
      <span
        data-testid="branch-badge"
        className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold"
        style={{ backgroundColor: "rgba(155,185,206,0.25)", color: "#1C4358" }}
      >
        <Icons.MapPin size={13} /> {branches[0].name}
      </span>
    );
  }
  return (
    <Select value={activeBranch || "all"} onValueChange={(v) => chooseBranch(v === "all" ? "" : v)}>
      <SelectTrigger data-testid="branch-selector-button" className="h-9 w-[170px] rounded-full text-xs font-semibold">
        <Icons.MapPin size={14} className="mr-1 text-[#1C4358]" />
        <SelectValue placeholder="All Branches" />
      </SelectTrigger>
      <SelectContent>
        {["global", "country"].includes(user?.scope_level) && <SelectItem value="all">All Branches</SelectItem>}
        {branches.map((b) => (
          <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const Topbar = ({ onOpenMobile, docReview }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { openHelp, suggestionSummary } = useHelp();
  const initials = (user?.full_name || "U").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <header
      className="sticky top-0 z-30 flex items-center gap-3 px-4 sm:px-6 h-16 border-b backdrop-blur"
      style={{ backgroundColor: "rgba(253,253,254,0.92)", borderColor: "var(--border)" }}
    >
      <Sheet>
        <SheetTrigger asChild>
          <button
            data-testid="mobile-menu-button"
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-lg hover:bg-[rgba(28,67,88,0.08)] text-[#1C4358]"
            onClick={onOpenMobile}
          >
            <Icons.Menu size={22} />
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="p-0 w-72 border-0" style={{ backgroundColor: "var(--mmm-navy)" }}>
          <div className="px-4 py-4 border-b border-white/10 text-white">
            <Logo size={36} withText textClass="text-white" />
          </div>
          <div className="overflow-y-auto h-[calc(100vh-72px)] mmm-scroll">
            <NavContentMobile docReview={docReview} />
          </div>
        </SheetContent>
      </Sheet>

      <h1 className="font-display text-lg sm:text-xl font-bold text-[#252221] truncate">
        {titleFromPath(location.pathname)}
      </h1>

      <div className="ml-auto flex items-center gap-2 sm:gap-3">
        <BranchSelector />
        <button
          data-testid="global-help-button"
          onClick={() => openHelp()}
          title="Help"
          className="relative inline-flex items-center justify-center h-9 w-9 rounded-lg hover:bg-[rgba(28,67,88,0.08)] text-[#1C4358]"
        >
          <Icons.LifeBuoy size={19} />
          {suggestionSummary?.total_to_review > 0 && (
            <span
              data-testid="help-suggestion-dot"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-[#B7791F] text-white text-[10px] font-bold flex items-center justify-center leading-none"
            >
              {suggestionSummary.total_to_review > 9 ? "9+" : suggestionSummary.total_to_review}
            </span>
          )}
        </button>
        <AccessibilityToolbar />
        <DocTriageMenu />
        <NotificationBell />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-button" className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-[rgba(28,67,88,0.06)]">
              <Avatar className="h-8 w-8">
                <AvatarFallback style={{ backgroundColor: "var(--mmm-navy)", color: "white", fontSize: 12 }}>
                  {initials}
                </AvatarFallback>
              </Avatar>
              <span className="hidden sm:block text-sm font-medium text-[#252221] max-w-[120px] truncate">
                {user?.full_name}
              </span>
              <Icons.ChevronDown size={15} className="text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="font-semibold">{user?.full_name}</div>
              <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
              <div className="mt-1 inline-flex text-[11px] px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(155,185,206,0.25)", color: "#1C4358" }}>
                {user?.role_label}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-[#9C1E22]">
              <Icons.LogOut size={16} className="mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

const NavContentMobile = ({ docReview }) => {
  // closes the sheet by navigating
  return <NavContent docReview={docReview} onNavigate={() => { /* sheet closes via outside click on navigation */ }} />;
};

export default function Layout() {
  const docReview = useDocReviewCount();
  return (
    <div className="flex min-h-screen" style={{ backgroundColor: "#F7FAFC" }}>
      <Sidebar docReview={docReview} />
      <div className="flex-1 min-w-0 flex flex-col">
        <Topbar docReview={docReview} />
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-5 sm:py-6 max-w-[1500px] w-full mx-auto mmm-fade-up">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
